/* AcyMart logo — sourced from the brand image assets in ./assets. */
import logoIcon from './assets/logo-icon.png';
import logoFull from './assets/logo.png';

export function LogoIcon({ size = 36 }) {
  return (
    <img
      src={logoIcon}
      alt="AcyMart"
      width={size}
      height={size}
      style={{ display: 'block', flexShrink: 0, objectFit: 'contain' }}
    />
  );
}

// Traced centerline of the orange arrow's curve in logo-icon.png's native
// 333x333 pixel space -- from the foot of the "A" (bottom-left), around the
// "C", to the arrowhead tip. Used only to drive the loader's travelling glow;
// the artwork itself (bag, handle, lettering) is never touched.
const LOADER_TRAIL_PATH = 'M 100 250 C 108 273, 152 291, 195 287 C 233 283, 263 269, 284 246 C 295 233, 305 221, 320 202';

/** The static logo mark with the whole traced arrow curve pulsing/glowing
    (breathing brighter and dimmer, in lockstep with a soft ambient halo
    behind it), used as the app's loading indicator. Everything except that
    traced curve — bag, handle, "AC" lettering, dot, background — is the
    untouched source image; only the overlaid SVG strokes and the halo
    behind it animate. */
export function AnimatedLogoIcon({ size = 64 }) {
  return (
    <span style={{ position: 'relative', display: 'inline-block', width: size, height: size, flexShrink: 0 }}>
      <img
        src={logoIcon}
        alt="AcyMart"
        width={size}
        height={size}
        style={{ display: 'block', objectFit: 'contain', position: 'relative' }}
      />
      <svg
        width={size}
        height={size}
        viewBox="0 0 333 333"
        aria-hidden="true"
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      >
        <defs>
          {/* Two layers pulse in lockstep, drawn along the full curve (no
              dash/travel) so the whole arrow glows at once: a wide blurred
              halo underneath, and a narrow near-white core on top so the
              light stays visible even where it crosses the logo's own
              orange arrow, not just against the white background. */}
          {/* Brand palette only: the sweep runs orange -> blue rather than the
              old orange/yellow/cyan, so the loader can't introduce a fifth and
              sixth hue on what is often the first frame a visitor sees. */}
          <linearGradient id="acy-loader-grad-glow" x1="100" y1="250" x2="320" y2="202" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="var(--color-brand-orange)" />
            <stop offset="55%" stopColor="var(--color-brand-orange)" stopOpacity="0.75" />
            <stop offset="100%" stopColor="var(--color-brand-blue)" />
          </linearGradient>
          <linearGradient id="acy-loader-grad-core" x1="100" y1="250" x2="320" y2="202" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="var(--color-primary-soft)" />
            <stop offset="55%" stopColor="var(--color-brand-white)" />
            <stop offset="100%" stopColor="var(--color-secondary-soft)" />
          </linearGradient>
          <filter id="acy-loader-glow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="7" />
          </filter>
        </defs>
        <path
          className="acy-loader-arrow-glow"
          d={LOADER_TRAIL_PATH}
          pathLength="100"
          fill="none"
          stroke="url(#acy-loader-grad-glow)"
          strokeLinecap="round"
          filter="url(#acy-loader-glow)"
        />
        <path
          className="acy-loader-arrow-core"
          d={LOADER_TRAIL_PATH}
          pathLength="100"
          fill="none"
          stroke="url(#acy-loader-grad-core)"
          strokeLinecap="round"
        />
        <circle className="acy-loader-travel-spark" r="7" fill="var(--color-brand-white)" filter="url(#acy-loader-glow)">
          <animateMotion dur="1.7s" repeatCount="indefinite" path={LOADER_TRAIL_PATH} keyTimes="0;0.08;0.9;1" keyPoints="0;0;1;1" calcMode="linear" />
          <animate attributeName="opacity" dur="1.7s" repeatCount="indefinite" values="0;1;1;0" keyTimes="0;0.08;0.9;1" />
          <animate attributeName="r" dur="1.7s" repeatCount="indefinite" values="3;6;6;2" keyTimes="0;0.08;0.9;1" />
        </circle>
      </svg>
    </span>
  );
}

export function BootScreen({ message }) {
  return (
    <div className="app-boot-loader">
      <AnimatedLogoIcon size={64} />
      <div className="abl-wordmark">Acy<span className="logo-store-text">Mart</span></div>
      {message && <p className="abl-message">{message}</p>}
    </div>
  );
}

export default function Logo({ width = 280 }) {
  const h = Math.round(width * 385 / 1135);
  return (
    <img
      src={logoFull}
      alt="AcyMart — Discover More. Shop Smarter."
      width={width}
      height={h}
      style={{ display: 'block', objectFit: 'contain' }}
    />
  );
}
